import { Store } from "@storeLocator/storeType";
import React, { useState, useEffect, useMemo } from "react";
import { convertTo12HourFormat } from "@storeLocator/utils/getStoreStatus";
import OpenclosingTime from "@components/onlineScheduler/OpenClosingTime";
import PrimarySubmitButton from "@ui/PrimarySubmitButton";
import { roundUpToNearest15Minutes } from "@components/onlineScheduler/utils/utilFunctions";
import { motion, AnimatePresence } from "framer-motion";
import LeadFormSimpleChangeStore from "./LeadFormSimpleChangeStore";
import { SecondaryButton } from "@ui";

interface LeadFormAppointmentComponentProps {
  selectedStore: Store;
  chooseStore: () => void;
  getTime: (time: Date) => void;
  threeStores: Store[];
  handleSelectStore: (store: Object) => void;
}

const LeadFormAppointmentComponent = ({
  selectedStore,
  chooseStore,
  getTime,
  threeStores,
  handleSelectStore,
}: LeadFormAppointmentComponentProps) => {
  const [appointmentDate, setAppointmentDate] = useState(new Date());
  const [appointmentTime, setAppointmentTime] = useState("");
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [choosingStore, setChoosingStore] = useState(false);

  // Function to handle form submission
  const onSubmit = (event: any) => {
    event.preventDefault();

    // Combine appointmentDate and appointmentTime into one Date object
    const [hours, minutes] = appointmentTime.split(":");
    const combinedDateTime = new Date(appointmentDate);

    // Set the hours and minutes of the combined date
    combinedDateTime.setHours(parseInt(hours));
    combinedDateTime.setMinutes(parseInt(minutes));
    const currentDateTime = new Date(); // Get current date and time

    // Validate if the selected date and time are in the future
    if (combinedDateTime < currentDateTime) {
      setErrorMessage(
        "The selected time cannot be earlier than the current time."
      );
    } else {
      setErrorMessage("");
      getTime(combinedDateTime);
    }
  };

  useEffect(() => {
    if (selectedStore) {
      const openHours = selectedStore.openingHours;
      const day = appointmentDate.getDay();
      const todayHours = openHours.find(
        (item: any) => item.dayOfWeek === day
      ) ?? {
        from1: "",
        to1: "",
      };
      const time = generateTimeSlot(todayHours.from1, todayHours.to1, 15);
      setTimeSlots(time);

      // Set the default appointment time to the rounded-up current time in 24-hour format
      const roundedCurrentTime = roundUpToNearest15Minutes(new Date());
      const formattedTime = roundedCurrentTime.toTimeString().substring(0, 5); // 24-hour format (HH:MM)
      setAppointmentTime(formattedTime);
    }
  }, [selectedStore, appointmentDate]);

  const generateTimeSlot = (
    fromTime: string,
    toTime: string,
    interval: number
  ) => {
    if (!fromTime || !toTime) return [];
    const slots = [];
    let startTime = new Date(`2021-01-01T${fromTime}:00`);
    const endTime = new Date(`2021-01-01T${toTime}:00`);
    for (
      let time = startTime;
      time < endTime;
      time.setMinutes(time.getMinutes() + interval)
    ) {
      const formattedTime = time.toTimeString().substring(0, 5); // 24-hour format (HH:MM)
      slots.push(formattedTime);
    }

    return slots;
  };

  return (
    <div>
      <h2 className="mb-[30px] text-left font-body text-[32px] font-bold leading-[36px] tracking-[0.64px] text-white md:mb-[10px] md:text-[48px] md:leading-[52px] md:tracking-[0.96px]">
        {!choosingStore ? (
          "Apply in person"
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={"text"}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
            >
              {"Select preferred store location."}
            </motion.div>
          </AnimatePresence>
        )}
      </h2>
      <form
        onSubmit={onSubmit}
        className="mt-[15px] flex flex-col md:mt-[10px]"
      >
        <div className="flex flex-col">
          <div className="flex flex-col gap-x-[20px] md:flex-row">
            {choosingStore && (
              <LeadFormSimpleChangeStore
                threeStores={threeStores}
                handleSelectStore={handleSelectStore}
                selectedStore={selectedStore}
                closeChooseStore={() => setChoosingStore(false)}
                chooseStore={() => setChoosingStore(true)}
              />
            )}
            {selectedStore ? (
              <div
                className={`mx-auto ${
                  choosingStore ? "mt-0 hidden md:block" : "mt-[5px]"
                } w-full max-w-[320px] grow md:order-1`}
              >
                <div className="rounded-[15px_15px_0px_0px] bg-cream p-5 ">
                  <h2 className="font-heading text-2xl font-bold leading-[26px] tracking-[1.44px] text-navy">
                    Your Preferred Store
                  </h2>
                </div>
                <div className="rounded-[0px_0px_15px_15px] bg-white p-5">
                  <div>
                    <p className="font-body text-base leading-[24px] tracking-[0.16px] text-creamyBlack">
                      {selectedStore.streetAndNumber}
                      <br />
                      {selectedStore.city}, {selectedStore.province}{" "}
                      {selectedStore.zip}
                      <br />
                      {selectedStore.cellphone && (
                        <>
                          <a
                            className="underline"
                            href={`tel:${selectedStore.cellphone}`}
                          >
                            {selectedStore.cellphone}
                          </a>
                          <br />
                        </>
                      )}
                      <OpenclosingTime
                        storeHours={selectedStore.openingHours}
                      />
                    </p>
                    <div className="mt-[10px] text-center">
                      <span
                        onClick={() => setChoosingStore(true)}
                        className="cursor-pointer font-body text-base font-bold leading-[24px] tracking-[0.16px] text-lightTeal underline"
                      >
                        Change Store
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              ""
            )}
          </div>
          {!choosingStore ? (
            <div className="mt-[30px] self-center">
              <PrimarySubmitButton
                externalText={"FINISH"}
                internalText={"SMART CHOICE"}
              />
            </div>
          ) : (
            <div className="mt-[30px] w-[250px] self-center md:-mt-[10px] md:self-start">
              <SecondaryButton
                onClick={() => setChoosingStore(false)}
                variant="light_pink_fill"
              >
                Save
              </SecondaryButton>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default LeadFormAppointmentComponent;
